import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useReferralId } from 'hooks/useReferralId';
import { useReviewPolling } from 'hooks/useReviewPolling';
import Referral from 'models/reviews/Referral';
import { referralQueryKeys, referralQueryParams } from 'services/api/reviews';

type ReferralContextType = {
  referral?: Referral;
  referralLoading: boolean;
  setSelectedReferralId: (referralId: string) => void;
  setAllowRefetch: (allow: boolean) => void;
};

const ReferralContext = createContext<ReferralContextType>({
  referral: undefined,
  referralLoading: true,
  setSelectedReferralId: () => {},
  setAllowRefetch: () => {},
});

export function ReferralProvider({ children }: { children: React.ReactNode }) {
  const referralId = useReferralId();
  const [selectedReferralId, setSelectedReferralId] = useState<string>(referralId ?? '');

  useEffect(() => {
    if (referralId) {
      setSelectedReferralId(referralId);
    }
  }, [referralId, setSelectedReferralId]);

  const { review, loading, setAllowRefetch } = useReviewPolling(
    selectedReferralId,
    referralQueryKeys,
    referralQueryParams
  );

  const value = useMemo(
    () => ({
      referral: review as Referral,
      referralLoading: loading,
      setSelectedReferralId,
      setAllowRefetch,
    }),
    [review, loading, setAllowRefetch]
  );

  return <ReferralContext value={value}>{children}</ReferralContext>;
}

export function useReferral() {
  const context = useContext(ReferralContext);

  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
}
